import { useCallback, useEffect, useMemo, useState } from "react";
const parseMMDDYYYY = (dateStr) => {
    const parts = dateStr.split("/");
    if (parts.length !== 3)
        return null;
    const monthValue = Number(parts[0]);
    const dayValue = Number(parts[1]);
    const yearValue = Number(parts[2]);
    if (!Number.isFinite(monthValue) || !Number.isFinite(dayValue) || !Number.isFinite(yearValue)) {
        return null;
    }
    const month = monthValue - 1;
    const day = dayValue;
    const year = yearValue;
    if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) {
        return null;
    }
    const candidateDate = new Date(year, month, day);
    if (Number.isNaN(candidateDate.getTime()) ||
        candidateDate.getFullYear() !== year ||
        candidateDate.getMonth() !== month ||
        candidateDate.getDate() !== day) {
        return null;
    }
    return {
        day,
        month,
        year
    };
};
const createProjectDate = (year, month, day, hours, minutes, seconds, ms) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
    return new Date(dateStr + "Z");
};
export const useHistoricalDateRange = ({ assetDrawerActive, defaultRange = "Last7Days", projectTimezone, timezoneOffset }) => {
    const [selectedDateRange, setSelectedDateRange] = useState(defaultRange);
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [previousDateRange, setPreviousDateRange] = useState(defaultRange);
    useEffect(() => {
        if (!assetDrawerActive) {
            setSelectedDateRange(defaultRange);
            setCustomFromDate(null);
            setCustomToDate(null);
            setPreviousDateRange(defaultRange);
            setShowCustomDatePicker(false);
        }
    }, [assetDrawerActive, defaultRange]);
    const handleCustomDateApply = useCallback((startDate, endDate) => {
        if (startDate && endDate) {
            const startParts = parseMMDDYYYY(startDate);
            const endParts = parseMMDDYYYY(endDate);
            if (startParts && endParts) {
                const from = createProjectDate(startParts.year, startParts.month, startParts.day, 0, 0, 0, 0);
                const to = createProjectDate(endParts.year, endParts.month, endParts.day, 23, 59, 59, 999);
                if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
                    setShowCustomDatePicker(false);
                    return;
                }
                setCustomFromDate(from);
                setCustomToDate(to);
                setSelectedDateRange("CustomDate");
                setPreviousDateRange("CustomDate");
            }
        }
        setShowCustomDatePicker(false);
    }, []);
    const handleCustomDateCancel = useCallback(() => {
        setShowCustomDatePicker(false);
        setSelectedDateRange(previousDateRange);
    }, [previousDateRange]);
    const handleDateRangeChange = useCallback((value) => {
        if (value === "CustomDate") {
            setPreviousDateRange(selectedDateRange);
            setShowCustomDatePicker(true);
        }
        else {
            setSelectedDateRange(value);
            setPreviousDateRange(value);
            setShowCustomDatePicker(false);
        }
    }, [selectedDateRange]);
    const { fromDateISO, toDateISO } = useMemo(() => {
        const tz = timezoneOffset.startsWith("UTC") ? timezoneOffset.substring(3) : timezoneOffset;
        const resolvedProjectTimezone = projectTimezone || "Europe/Amsterdam";
        const getTodayInProjectTz = () => {
            const now = new Date();
            const parts = new Intl.DateTimeFormat("en-US", {
                day: "2-digit",
                month: "2-digit",
                timeZone: resolvedProjectTimezone,
                year: "numeric"
            }).formatToParts(now);
            return {
                day: parseInt(parts.find((p) => p.type === "day").value, 10),
                month: parseInt(parts.find((p) => p.type === "month").value, 10),
                year: parseInt(parts.find((p) => p.type === "year").value, 10)
            };
        };
        const startOf = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T00:00:00.000${tz}`;
        const endOf = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T23:59:59.999${tz}`;
        const shiftDays = (y, m, d, days) => {
            const dt = new Date(Date.UTC(y, m - 1, d + days));
            return { day: dt.getUTCDate(), month: dt.getUTCMonth() + 1, year: dt.getUTCFullYear() };
        };
        const makeResult = (fromIso, toIso) => ({ fromDateISO: fromIso, toDateISO: toIso });
        if (selectedDateRange === "CustomDate" && customFromDate && customToDate) {
            const fy = customFromDate.getUTCFullYear();
            const fm = customFromDate.getUTCMonth() + 1;
            const fd = customFromDate.getUTCDate();
            const ty = customToDate.getUTCFullYear();
            const tm = customToDate.getUTCMonth() + 1;
            const td = customToDate.getUTCDate();
            return makeResult(startOf(fy, fm, fd), endOf(ty, tm, td));
        }
        const { day, month, year } = getTodayInProjectTz();
        switch (selectedDateRange) {
            case "Today":
                return makeResult(startOf(year, month, day), endOf(year, month, day));
            case "Yesterday": {
                const yest = shiftDays(year, month, day, -1);
                return makeResult(startOf(yest.year, yest.month, yest.day), endOf(yest.year, yest.month, yest.day));
            }
            case "CurrentWeek": {
                const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
                const weekStart = shiftDays(year, month, day, -dow);
                return makeResult(startOf(weekStart.year, weekStart.month, weekStart.day), endOf(year, month, day));
            }
            case "PreviousWeek": {
                const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
                const thisSunday = shiftDays(year, month, day, -dow);
                const prevSunday = shiftDays(thisSunday.year, thisSunday.month, thisSunday.day, -7);
                const prevSaturday = shiftDays(thisSunday.year, thisSunday.month, thisSunday.day, -1);
                return makeResult(startOf(prevSunday.year, prevSunday.month, prevSunday.day), endOf(prevSaturday.year, prevSaturday.month, prevSaturday.day));
            }
            case "CurrentMonth":
                return makeResult(startOf(year, month, 1), endOf(year, month, day));
            case "Last30Days": {
                const f = shiftDays(year, month, day, -29);
                return makeResult(startOf(f.year, f.month, f.day), endOf(year, month, day));
            }
            case "Last90Days": {
                const f = shiftDays(year, month, day, -89);
                return makeResult(startOf(f.year, f.month, f.day), endOf(year, month, day));
            }
            case "Last180Days": {
                const f = shiftDays(year, month, day, -179);
                return makeResult(startOf(f.year, f.month, f.day), endOf(year, month, day));
            }
            case "Last365Days": {
                const f = shiftDays(year, month, day, -364);
                return makeResult(startOf(f.year, f.month, f.day), endOf(year, month, day));
            }
            case "Last7Days":
            default: {
                const f = shiftDays(year, month, day, -6);
                return makeResult(startOf(f.year, f.month, f.day), endOf(year, month, day));
            }
        }
    }, [selectedDateRange, customFromDate, customToDate, timezoneOffset, projectTimezone]);
    return {
        customFromDate,
        customToDate,
        fromDateISO,
        handleCustomDateApply,
        handleCustomDateCancel,
        handleDateRangeChange,
        selectedDateRange,
        showCustomDatePicker,
        toDateISO
    };
};
